import { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { admin, custom, departments, externalTypes, scopeActions, scopes, services } from "data/authData";
import { getFlatRoleKeys, getRoleScopes } from "../Users/utils/roleHelpers";
import { AuthState } from "../../context/AuthContext";
import { GlobalState } from "../../context/GlobalContext";
import AuthenticatorSection from "./AuthenticatorSection";
import DeviceSection from "./DeviceSection";
import "./profile.css";

const catalogLabel = (catalog, key) => {
	const match = Object.values(catalog || {}).find((item) => item?.key === key);
	return match?.name || "";
};

const roleLabel = (key) => {
	if (key === admin) return "Admin";
	return (
		catalogLabel(departments, key) ||
		catalogLabel(services, key) ||
		catalogLabel(custom, key) ||
		catalogLabel(externalTypes, key) ||
		String(key || "").replace(/_/g, " ")
	);
};

const scopeByKey = Object.values(scopes).reduce((acc, scope) => {
	acc[scope.key] = scope;
	return acc;
}, {});

const actionLabel = (actionKey) => {
	const match = Object.values(scopeActions).find((action) => action.key === actionKey);
	return match?.name || String(actionKey || "").toUpperCase();
};

const optionLabel = (scope, value) => scope?.options?.find((option) => option.key === value)?.name || value;

const prettyLabel = (value) =>
	String(value || "")
		.replace(/_/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase()
		.split(" ")
		.map((word) => {
			if (["hr", "mdm", "mrp", "id"].includes(word)) return word.toUpperCase();
			if (word === "&" || word === "/") return word;
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(" ");

const parseRole = (role) => {
	if (!role) return {};
	if (typeof role === "string") {
		try {
			return parseRole(JSON.parse(role));
		} catch (error) {
			return {};
		}
	}
	if (Array.isArray(role)) {
		return role.reduce((acc, key) => {
			if (typeof key === "string" && key) acc[key] = true;
			return acc;
		}, {});
	}
	if (typeof role === "object") return role;
	return {};
};

const detailValue = (value) => {
	if (value === 0) return "0";
	if (value == null || value === "") return "—";
	return String(value);
};

const Detail = ({ label, value, format }) => {
	const text = detailValue(value);
	if (text === "—") return null;
	return (
		<div className="profile-detail-row">
			<span className="profile-detail-label">{label}</span>
			<span className="profile-detail-value">{format ? format(text) : text}</span>
		</div>
	);
};

const Profile = ({ setModalOpen }) => {
	const { user } = AuthState();
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [section, setSection] = useState("account");
	const [defaultMethod, setDefaultMethod] = useState("email");
	const [savingDefault, setSavingDefault] = useState(false);
	const role = parseRole(user?.role);
	const roleKeys = getFlatRoleKeys(role);
	const roleScopes = getRoleScopes(role);
	const initials = String(user?.name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part[0])
		.join("")
		.toUpperCase();

	useEffect(() => {
		if (section !== "signin") return undefined;
		let active = true;
		axios
			.get("/api/auth2/default_login_method")
			.then((res) => {
				if (active) setDefaultMethod(res?.data?.result?.method || "email");
			})
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [section]);

	const setAsDefault = async (method) => {
		setSavingDefault(true);
		try {
			const res = await axios.post("/api/auth2/default_login_method", { method });
			setDefaultMethod(res?.data?.result?.method || method);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: res?.data?.message || "Default sign-in method updated", severity: "success" });
		} catch (error) {
			const message = error?.response?.data?.message || "Could not update the default sign-in method";
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message, severity: "error" });
		} finally {
			setSavingDefault(false);
		}
	};

	const DefaultControl = ({ method }) =>
		defaultMethod === method ? (
			<p className="profile-default-note">Default at login</p>
		) : (
			<button type="button" className="profile-default-btn" disabled={savingDefault} onClick={() => setAsDefault(method)}>
				Set as default
			</button>
		);

	return (
		<Box className="profile-modal">
			<div className="profile-modal-top">
				<header className="profile-modal-head">
					<div className="profile-identity">
						<div className="profile-avatar" aria-hidden>
							{initials || "?"}
						</div>
						<div style={{ minWidth: 0 }}>
							<h2 className="profile-name">{user?.name || "Signed-in user"}</h2>
							<p className="profile-email">{[user?.designation, user?.email].filter(Boolean).join(" · ") || "—"}</p>
						</div>
					</div>
					<IconButton aria-label="Close profile" onClick={() => setModalOpen(false)} size="small">
						<CloseRoundedIcon />
					</IconButton>
				</header>

				<div className="profile-switch" role="tablist" aria-label="Profile sections">
					<button type="button" role="tab" className={section === "account" ? "is-active" : ""} onClick={() => setSection("account")}>
						Account
					</button>
					<button type="button" role="tab" className={section === "signin" ? "is-active" : ""} onClick={() => setSection("signin")}>
						Sign-in
					</button>
				</div>
			</div>

			<div className="profile-body">
				{section === "account" ? (
					<div className="profile-account">
						<section>
							<h3 className="profile-section-title">Your details</h3>
							<div className="profile-details">
								<Detail label="Email" value={user?.email} />
								<Detail label="Mobile" value={user?.mobile_no} />
								<Detail label="Department" value={user?.department} />
								<Detail label="Designation" value={user?.designation} />
								<Detail label="Employee ID" value={user?.employee_id} />
								<Detail label="Account" value={user?.type} format={prettyLabel} />
							</div>
						</section>

						<section className="profile-access">
							<h3 className="profile-section-title">Roles</h3>
							{roleKeys.length ? (
								<div className="profile-chips">
									{roleKeys.map((key) => (
										<span className="profile-chip" key={key}>
											{roleLabel(key)}
										</span>
									))}
								</div>
							) : (
								<p className="profile-copy">No roles assigned.</p>
							)}
							{Object.keys(roleScopes).length ? (
								<div className="profile-scopes">
									{Object.entries(roleScopes).map(([scopeKey, actionMap]) => {
										const scope = scopeByKey[scopeKey];
										return (
											<div className="profile-scope" key={scopeKey}>
												<div className="profile-scope-name">{scope?.name || scopeKey.replace(/_/g, " ")}</div>
												{Object.entries(actionMap).map(([actionKey, values]) => (
													<div className="profile-scope-line" key={actionKey}>
														{actionLabel(actionKey)}: {(values || []).map((value) => optionLabel(scope, value)).join(", ")}
													</div>
												))}
											</div>
										);
									})}
								</div>
							) : null}
						</section>
					</div>
				) : (
					<div className="profile-methods">
						<section className="profile-method">
							<div className="profile-method-head">
								<div className="profile-method-title">Email</div>
								<span className="profile-status is-on">On</span>
							</div>
							<p className="profile-copy">
								A 6-digit code is sent to {user?.email || "your email"} after your password. This stays available even when another method is the default.
							</p>
							<DefaultControl method="email" />
						</section>
						<section className="profile-method">
							<div className="profile-method-head">
								<div className="profile-method-title">Authenticator app</div>
							</div>
							<AuthenticatorSection isDefault={defaultMethod === "totp"} onSetDefault={() => setAsDefault("totp")} onDefaultCleared={() => setDefaultMethod("email")} />
						</section>
						<section className="profile-method">
							<div className="profile-method-head">
								<div className="profile-method-title">This laptop</div>
							</div>
							<DeviceSection isDefault={defaultMethod === "webauthn"} onSetDefault={() => setAsDefault("webauthn")} onDefaultCleared={() => setDefaultMethod("email")} />
						</section>
					</div>
				)}
			</div>
		</Box>
	);
};

export default Profile;
